import { useCallback, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { useCompanyName } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useCompanyName";
import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import useCreateThreadQuery from "app/pages/sales-intelligence/modules/ai-agent/queries/useCreateThreadQuery";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type {
    AiAgentMeetingContext,
    AiAgentMeetingPayload,
    FullThread,
} from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { proNavigator } from "single-spa/react-app/ProNavigator";

export type MeetingBrief = {
    data: string;
    threadId: string;
    messageId: string;
    isFetchedAfterMount: boolean;
};

type MeetingBriefGeneration = {
    brief: MeetingBrief;
    generate: (payload: AiAgentMeetingPayload) => void;
    retry: (meetingContext: AiAgentMeetingContext) => void;
    canRetry: boolean;
};

export const useMeetingBriefGeneration = (): MeetingBriefGeneration => {
    const queryClient = useQueryClient();
    const {
        params: { domain, id },
    } = useCurrentStateAndParams<{ domain: string; id?: string }>();
    const { companyName, isReady: isCompanyReady } = useCompanyName();
    const [payload, setPayload] = useState<AiAgentMeetingPayload | null>(null);

    const { data, threadId, messageId, isFetchedAfterMount, reset } = useCreateThreadQuery(id ? null : payload);

    useEffect(() => {
        if (!threadId || !payload) {
            return;
        }
        const thread: FullThread = {
            domain,
            meetingContext: payload.meetingContext,
            messages: [],
        };
        queryClient.setQueryData(aiAgentQueryKeys.threadById(threadId), thread);
        proNavigator.updateQueryParams({ id: threadId }, { reload: false });
        void queryClient.invalidateQueries({ queryKey: aiAgentQueryKeys.threads() });
    }, [threadId]);

    const generate = useCallback((nextPayload: AiAgentMeetingPayload): void => {
        setPayload(nextPayload);
        AIAgentTrackingService.meeting.trackGenerateThread(nextPayload.meetingContext.attendeesCount);
    }, []);

    const retry = (meetingContext: AiAgentMeetingContext): void => {
        if (!isCompanyReady) {
            return;
        }
        reset();
        generate({ domain, companyName, meetingContext });
        proNavigator.updateQueryParams({ id: undefined }, { reload: false });
    };

    return {
        brief: { data, threadId, messageId, isFetchedAfterMount },
        generate,
        retry,
        canRetry: isCompanyReady,
    };
};
